import { useState } from "react";
import { Globe, Trash2 } from "lucide-react";
import { Badge } from "../../components/ui/badge";
import { Modal } from "../../components/ui/modal";
import { MultiSelect } from "../../components/ui/multi-select";
import { useToast } from "../../components/ui/toast";
import { cn } from "../../lib/utils";
import { WEBHOOK_EVENTS, type Webhook } from "./types";

export function WebhooksTab({ webhooks, onAdd, onToggle, onDelete }: {
  webhooks: Webhook[];
  onAdd: (url: string, events: string[]) => void;
  onToggle: (id: string) => void;
  onDelete: (id: string) => void;
}) {
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState("");
  const [events, setEvents] = useState<Set<string>>(new Set(["call.completed"]));
  const validUrl = /^https:\/\/.+\..+/.test(url);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-ink">Webhooks</h2>
        <button onClick={() => setOpen(true)}
          className="rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-hover">
          Add Endpoint
        </button>
      </div>

      {webhooks.length === 0 && (
        <div className="rounded-xl border-2 border-dashed border-line p-12 text-center">
          <Globe className="mx-auto h-10 w-10 text-line" />
          <p className="mt-3 text-lg font-bold text-ink">No webhooks configured</p>
          
        </div>
      )}

      <ul className="space-y-3">
        {webhooks.map((w) => (
          <li key={w.id} className="rounded-xl shadow-card dark:shadow-card-dark bg-paper-card dark:bg-[#292524] p-4">
            <div className="flex items-center gap-3">
              <code className="flex-1 truncate font-mono text-sm text-ink">{w.url}</code>
              <button
                role="switch" aria-checked={w.active} aria-label={`Toggle ${w.url}`}
                onClick={() => onToggle(w.id)}
                className={cn("relative h-5 w-9 rounded-full transition-colors", w.active ? "bg-success" : "bg-line")}
              >
                <span className={cn("absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all", w.active ? "left-[18px]" : "left-0.5")} />
              </button>
              <button
                aria-label={`Delete ${w.url}`}
                onClick={() => { if (window.confirm("Delete this endpoint?")) { onDelete(w.id); toast.danger("Webhook deleted"); } }}
                className="rounded-lg p-2 text-ink-3 hover:bg-danger-soft hover:text-danger"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {w.events.map((e) => <Badge key={e} tone="neutral" className="font-mono">{e}</Badge>)}
              <span className="ml-auto text-xs text-ink-3">
                {w.active ? (w.lastDelivery ? `Last delivery ${w.lastDelivery}` : "Awaiting first event") : "Paused"}
              </span>
            </div>
          </li>
        ))}
      </ul>

      {open && (
        <Modal
          title="Add Webhook Endpoint"
          onClose={() => setOpen(false)}
          width="max-w-md"
          footer={
            <>
              <button onClick={() => setOpen(false)} className="rounded-lg border border-line px-4 py-2 text-sm font-semibold text-ink hover:bg-soft">Cancel</button>
              <button
                disabled={!validUrl || events.size === 0}
                onClick={() => { onAdd(url, [...events]); toast.success("Webhook endpoint added"); setOpen(false); setUrl(""); }}
                className={validUrl && events.size > 0 ? "rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-hover" : "cursor-not-allowed rounded-lg bg-line px-4 py-2 text-sm font-semibold text-ink-3"}
              >
                Save Endpoint
              </button>
            </>
          }
        >
          <div className="space-y-4">
            <label className="block">
              <span className="mb-1 block font-mono text-[10px] font-semibold uppercase tracking-wider text-ink-3">HTTPS URL</span>
              <input
                value={url} onChange={(e) => setUrl(e.target.value)}
                placeholder="https://yourapp.com/webhooks/techladder"
                className={cn("w-full rounded-lg border px-3 py-2.5 font-mono text-sm text-ink focus:outline-none focus:ring-2 focus:ring-brand/30", validUrl ? "border-line focus:border-brand" : "border-danger/40")}
              />
              {!validUrl && url && <span className="mt-1 block text-xs text-danger">Must be a valid HTTPS URL.</span>}
            </label>
            <div>
              <span className="mb-1 block font-mono text-[10px] font-semibold uppercase tracking-wider text-ink-3">Subscribed Events</span>
              <MultiSelect
                buttonLabel="Events"
                options={WEBHOOK_EVENTS.map((e) => ({ value: e, label: e }))}
                selected={events}
                defaultSelected={[...WEBHOOK_EVENTS]}
                onChange={setEvents}
              />
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
